import DeleteIcon from "@mui/icons-material/Delete";
import HideImageIcon from "@mui/icons-material/HideImage";
import ReportIcon from "@mui/icons-material/Report";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Divider, Grow, Tooltip, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Cookies from "js-cookie";
import React from "react";
import { useTranslation } from "react-i18next";
import { Images } from "../../../../assets";
import { useCommonContext } from "../../../../context/juno/CommonContext";
import { ImageInfo } from "../../../../types/junoTypes";
import { localDate } from "../../../../utils/utils";
import ActionLabels from "./ActionLabels";

interface ImagesOnlyViewProps {
  requests: [ImageInfo][];
  setOpenModal: (open: boolean) => void;
  setSelectedImageUuid: (uuid: string | null) => void;
  handleDialogOpen: (uuid: string) => void;
}

const ImagesOnlyView: React.FC<ImagesOnlyViewProps> = ({
  requests,
  setOpenModal,
  setSelectedImageUuid,
  handleDialogOpen,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const uiLanguage = Cookies.get("ui_language") || "en";
  const { filtered, setFiltered } = useCommonContext();

  // 成功した画像の表示
  const renderSuccessImage = (image: ImageInfo) => {
    return (
      <Box sx={{ position: "relative", borderRadius: "8px", overflow: "hidden" }}>
        <Box
          sx={{
            display: "inline-flex",
            transition: "0.2s ease!important",
            "&:hover": {
              cursor: "pointer",
              transform: "scale(1.05)",
            },
          }}
        >
          <img
            src={image.is_oversized ? Images.oversizedImage : image.is_r2 ? image.url : image.url + "public"}
            alt="Generated"
            style={{
              width: "100%",
              height: "100%",
              maxHeight: "300px",
              // objectFit: "cover",
              borderRadius: "8px",
              filter: image.safe_level > 2 && filtered ? "blur(40px)" : "none",
              transform: image.safe_level > 2 && filtered ? "scale(1.5)" : "none",
              transition: "0.2s ease",
            }}
          />
        </Box>
        {image.safe_level > 2 && filtered && (
          // フィルタリングされた画像の表示
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "white",
              zIndex: 2,
              width: "100%",
              p: 2,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <ReportIcon sx={{ mr: 1 }} color={"warning"} />
                <Typography variant={"body2"}>{t("juno.input.filtered")}</Typography>
              </Box>
              <Typography variant={"caption"} whiteSpace={"break-spaces"} sx={{ textAlign: "center" }}>
                {t("juno.input.filteredDescription")}
              </Typography>
            </Box>
          </Box>
        )}
        {image.safe_level > 2 && (
          <Tooltip title={filtered ? t("juno.input.hide") : t("juno.input.show")}>
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFiltered(!filtered);
              }}
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 3, color: "white" }}
              aria-label="toggle blur"
              size={"small"}
            >
              {filtered ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // 処理中の表示
  const renderProcessingBox = () => {
    return (
      <Box
        sx={{
          p: 2,
          alignItems: "center",
          width: "250px",
          height: "200px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          bgcolor: theme.palette.background.default,
          borderRadius: 2,
        }}
      >
        <Typography variant={"body1"} fontWeight={"500"} sx={{ mb: 2 }}>
          {t("juno.history.generating")}
        </Typography>
        <CircularProgress size={20} thickness={4} sx={{ mb: 2 }} />
        <Typography
          variant={"caption"}
          color={theme.palette.text.secondary}
          sx={{ whiteSpace: "break-spaces", mx: 2, textAlign: "center" }}
        >
          {t("juno.history.generatingDescription")}
        </Typography>
      </Box>
    );
  };

  // 失敗時の表示
  const renderErrorBox = () => {
    return (
      <Box
        sx={{
          p: 2,
          alignItems: "center",
          width: "250px",
          height: "200px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          bgcolor: theme.palette.background.default,
          borderRadius: 2,
        }}
      >
        <Typography variant={"body1"} fontWeight={"500"} sx={{ mb: 2 }}>
          No Images Found
        </Typography>
        <HideImageIcon color={"error"} sx={{ mb: 2 }} />
        <Typography variant={"caption"} color={theme.palette.text.secondary} sx={{ px: 2, textAlign: "center" }}>
          Sorry, something went wrong. Please try again later.
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {requests.map((request, requestIndex) => {
        return (
          <Box key={requestIndex}>
            <Box
              mb={2}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box gap={1} sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <ActionLabels params={request[0].params} />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  width: "100%",
                }}
              >
                {request[0].params.prompt}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary", whiteSpace: "nowrap", mr: 1 }}>
                {request[0]["created_at"] && localDate(request[0]["created_at"], uiLanguage)}
              </Typography>
              <Tooltip title={t("common.delete")} placement={"top"}>
                <span>
                  <IconButton
                    color={"error"}
                    disabled={request[0].request_status === "Processing"}
                    onClick={() => handleDialogOpen(request[0].request_uuid)}
                    size="small"
                  >
                    <DeleteIcon fontSize={"small"} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", overflow: "auto", gap: 2 }}>
              {request[0].request_status === "Success"
                ? // 成功したリクエストは画像を表示
                  request.map((image, imageIndex) => (
                    <Grow key={imageIndex} in={true} timeout={1000}>
                      <Box
                        key={image.uuid || `image-${imageIndex}`}
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          cursor: "pointer",
                          position: "relative",
                          boxShadow: 1,
                        }}
                        onClick={() => {
                          setSelectedImageUuid(image.uuid);
                          setOpenModal(true);
                        }}
                      >
                        {renderSuccessImage(image)}
                      </Box>
                    </Grow>
                  ))
                : request[0].request_status === "Processing"
                  ? // 処理中のリクエストは進行中の表示
                    renderProcessingBox()
                  : // 失敗したリクエストはエラー表示
                    renderErrorBox()}
            </Box>
            {requestIndex < requests.length - 1 && <Divider sx={{ mt: 2, mb: 2 }} />}
          </Box>
        );
      })}
    </Box>
  );
};

export default ImagesOnlyView;
